import { useWeb3React } from '@web3-react/core'
import { useTranslation } from 'contexts/Localization'
import React, { useMemo } from 'react'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import { CardFooter, Button, Text, useModal, Flex, ArrowForwardIcon, Skeleton, useTooltip, InfoIcon } from 'vvs-uikit'
import Dots from 'components/Loader/Dots'
import { AutoColumn } from 'components/Layout/Column'
import BoostFarmQAModal from 'views/xVvs/component/BoostFarmQA'
import { Link } from 'react-router-dom'
import { useFarms } from 'state/farms/hooks'
import { TokenPairText, TokenWrapper } from 'views/BoostCalculator/styles'
import { TokenPairImage } from 'components/TokenImage'
import { useGetBoostedFarms } from 'hooks/boost-farm/useFarmBoostContract'
import useTheme from 'hooks/useTheme'
import { useTokenPairOptions } from 'components/TokenPairSelector/useTokenPairOptions'
import useBoostFactor from 'hooks/boost-farm/useBoostFactor'
import { Token } from 'vvs-sdk'
import BoostFarmModal from 'views/Farms/components/BoostFarmModal'
import { getTotalApr, getDisplayApr, getBoostedDisplayApr } from 'views/Farms/utils'
import { BoostExpiredTag } from 'components/Tags'
import { Body } from '../../styles'
import BoostFarmSelectModal from './BoostFarmSelectModal'
import DepositXvvsButton from './DepositXvvsButton'

interface BoostedFarmCardProps {
  farmId: number
  primaryToken: Token
  secondaryToken: Token
  pairLabel: string
  vvsRewardsApr: number
  tokenRewardsApr: number
  lpRewardsApr: number
}
const BoostedFarmCard: React.FC<BoostedFarmCardProps> = ({
  farmId,
  primaryToken,
  secondaryToken,
  pairLabel,
  vvsRewardsApr,
  tokenRewardsApr,
  lpRewardsApr,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const { data: farmsLP } = useFarms()
  const { data: boostFactorData, isLoading: isBoostFactorDataLoading } = useBoostFactor({ farmId }, true)
  const [onPresentBoostFarmModal] = useModal(<BoostFarmModal currentFarmId={farmId} />)

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Your APR% remain the same because you do not have any staked liquidity in the selected farm.  You can continue applying the boost and stake liquidity in this farm later to enjoy the boost APR%',
    ),
    { placement: 'top-end', tooltipOffset: [20, 10] },
  )

  const handleClickBoost = async () => {
    onPresentBoostFarmModal()
  }

  const baseApr = getTotalApr(tokenRewardsApr, lpRewardsApr)

  return (
    <Flex padding="18px" gap="18px" flexDirection="column" background={theme.colors.backgroundAlt} borderRadius="12px">
      <Flex alignItems="center" justifyContent="flex-start" gap="4px">
        <TokenWrapper>
          <TokenPairImage
            primaryToken={primaryToken}
            secondaryToken={secondaryToken}
            variant="inverted"
            width={40}
            height={40}
          />
        </TokenWrapper>
        <TokenPairText>{pairLabel?.split(' ')[0]}</TokenPairText>
        {farmsLP?.find((f) => f.pid === farmId)?.boostFarmExpired && <BoostExpiredTag />}
      </Flex>
      <Flex flexDirection="column" gap="6px">
        <Flex justifyContent="space-between" alignItems="center">
          <Text small color="textSubtle">
            {t('Boosted APR')}
          </Text>
          <Flex alignItems="center" gap="5px" justifyContent="center">
            {baseApr > 0 ? (
              <Text fontSize="16px" fontWeight="600" lineHeight="24px">
                {getDisplayApr(tokenRewardsApr, lpRewardsApr, 1)}%
              </Text>
            ) : (
              <Skeleton width="40px" />
            )}
            <ArrowForwardIcon width="24px" height="24px" color={theme.colors.text} />
            {!isBoostFactorDataLoading && boostFactorData?.boostFactor && baseApr > 0 ? (
              <Text fontSize="16px" fontWeight="600" lineHeight="24px">
                {getBoostedDisplayApr(vvsRewardsApr, tokenRewardsApr, lpRewardsApr, boostFactorData?.boostFactor, 1, 1)}
                %
              </Text>
            ) : (
              <Skeleton width="40px" />
            )}
            {!isBoostFactorDataLoading && boostFactorData?.boostFactor === 1 && (
              <Flex ref={targetRef}>
                <InfoIcon color="primary" />
                {tooltipVisible && tooltip}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Text small color="textSubtle" textTransform="uppercase">
            {t('boost factor')}
          </Text>
          {!isBoostFactorDataLoading && boostFactorData?.boostFactor ? (
            <Text fontSize="16px" fontWeight="600" lineHeight="24px">
              {boostFactorData?.boostFactor}x
            </Text>
          ) : (
            <Skeleton width="40px" />
          )}
        </Flex>
      </Flex>
      <Flex justifyContent="flex-start">
        <Button scale="sm" variant="secondary" onClick={handleClickBoost}>
          {t('Boost Another Farm')}
        </Button>
      </Flex>
    </Flex>
  )
}
const BoostBody: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { boostData, userDataLoaded } = useUserVvsBoost()
  const { stakes } = boostData

  const { data: boostedFarms, isLoading: isBoostedFarmsLoading } = useGetBoostedFarms()

  const activeStakes = stakes?.filter((x) => x.active)

  const [onPresentBoostFarmQAModal] = useModal(<BoostFarmQAModal />)
  const [onPresentBoostFarmSelectModal] = useModal(<BoostFarmSelectModal />)

  const { data: farmsLP } = useFarms()

  const { pairOptions } = useTokenPairOptions()

  const boostedFarmsDetails = useMemo(() => {
    const activeFarms = farmsLP.filter((farm) => farm.pid !== 0 && !farm.isFinished && farm.enableBoost)
    return activeFarms.filter((farm) => {
      return boostedFarms?.find((item) => item === farm.pid)
    })
  }, [boostedFarms, farmsLP])

  const renderBody = () => {
    if (!account) {
      return (
        <Text color="textSubtle" textAlign="center">
          {t('Connect to a wallet to view your boosted farms.')}
        </Text>
      )
    }
    if (!userDataLoaded || isBoostedFarmsLoading) {
      return (
        <Text color="textSubtle" textAlign="center">
          <Dots>{t('Loading')}</Dots>
        </Text>
      )
    }

    if (activeStakes?.length === 0) {
      return (
        <AutoColumn gap="8px">
          <Text bold textAlign="center">
            {t('No Farm Boosted')}
          </Text>
          <Text color="textSubtle" textAlign="center" fontSize="13px">
            {t('Depositing xVVS gives you power to boost LP farm.')}
          </Text>
          <Flex justifyContent="center" alignItems="center" mt="8px">
            <Button variant="secondary" onClick={onPresentBoostFarmQAModal}>
              {t('Learn More')}
            </Button>
          </Flex>
        </AutoColumn>
      )
    }

    if (!boostedFarmsDetails || boostedFarmsDetails?.length === 0) {
      return (
        <AutoColumn gap="8px">
          <Text bold textAlign="center">
            {t('You are available to boost your farm!')}
          </Text>
          <Text color="textSubtle" textAlign="center" fontSize="13px">
            {t(
              'Depositing xVVS gives you power to boost LP farm. You can get additional boosted farm APR% depending on your xVVS deposit amount and duration selected.',
            )}
          </Text>
          <Flex justifyContent="center" alignItems="center" mt="8px">
            <Button variant="secondary" onClick={onPresentBoostFarmQAModal}>
              {t('Learn More')}
            </Button>
          </Flex>
        </AutoColumn>
      )
    }

    return (
      <Flex gap="32px" flexDirection="column">
        {boostedFarmsDetails?.map((farm) => {
          const currentOption = pairOptions.find((option) => option.id === farm.pid)
          return (
            <BoostedFarmCard
              key={farm?.pid}
              farmId={farm?.pid}
              primaryToken={farm?.token}
              secondaryToken={farm?.quoteToken}
              pairLabel={farm?.lpSymbol.split(' ')[0]}
              vvsRewardsApr={currentOption.vvsRewardsApr}
              tokenRewardsApr={currentOption.tokenRewardsApr}
              lpRewardsApr={currentOption.lpRewardsApr}
            />
          )
        })}
      </Flex>
    )
  }

  return (
    <>
      <Body>{renderBody()}</Body>
      {activeStakes?.length !== 0 ? (
        boostedFarms?.length !== 2 && (
          <CardFooter style={{ textAlign: 'center' }}>
            <Button
              id="boost-pool-button"
              scale="lg"
              as={Link}
              to="#"
              width="100%"
              disabled={!userDataLoaded}
              onClick={userDataLoaded && onPresentBoostFarmSelectModal}
            >
              <Text color="white" bold as="h2">
                {t('Boost LP Farm')}
              </Text>
            </Button>
          </CardFooter>
        )
      ) : (
        <DepositXvvsButton />
      )}
    </>
  )
}

export default BoostBody
